/* implKeys.js — THE ONE SPELLING OF AN IMPLEMENTATION LINE'S SYNTHETIC KEY, and the two
 * questions the codebase asks about it.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────────────────
 * `rev_impl_<id>` was spelled inline at SEVEN client sites across five files, and the
 * routing rule it encodes was re-derived at each. On 2026-09-15 one of those derivations
 * was measured WRONG — `buildISLines`' leaf filter asked "is the target NULL?" when the
 * question is "is the target something OTHER than this row's own synthetic?", so the
 * keep-a-separate-line SENTINEL suppressed its own leaf and cid=36 lost $75,000.15 of
 * forecast revenue off the income statement. The fix was one line; this file is the reason
 * the next reader does not have to find the rule in five places to know it.
 *
 * ── THE THREE STATES, WHICH IS THE WHOLE POINT ────────────────────────────────────────
 *   target_gl_line_key === null           UNANSWERED   -> emits its own synthetic line
 *   target_gl_line_key === `rev_impl_<id>` SEPARATE    -> emits its own synthetic line
 *   target_gl_line_key === anything else   ROUTED      -> emits NOTHING; the money lands
 *                                                         on the operator's named IS leaf
 * ★ NULL and the SENTINEL take the SAME path DELIBERATELY (Part D): the sentinel records
 *   that the operator ANSWERED, without adding an engine branch. ⛔ A two-state truthiness
 *   test cannot express that, and every time one is written here it will be wrong again.
 *
 * ── TWO FUNCTIONS, NOT ONE, AND THAT IS DELIBERATE ────────────────────────────────────
 * ⛔ These are RELATED BUT NOT THE SAME RULE, and collapsing them would mean a caller
 *   passing a flag to choose a RETURN TYPE:
 *     implTargetKey     answers "WHICH KEY does this line deposit at?"   -> string
 *     isRoutedElsewhere answers "IS this line routed somewhere else?"    -> boolean
 *   What they genuinely SHARE is `implSyntheticKey`, and that is what is extracted.
 *
 * ── THE ARGUMENT SHAPE, MEASURED ──────────────────────────────────────────────────────
 * ⛔ These take `(id, target)` rather than a line OBJECT, because the five call sites do
 *   not all hold one: `runFullModel` and `orchestrator` iterate
 *   `Object.entries(implementationRevenue)`, so the id is the MAP KEY (a STRING) and the
 *   value carries no `.id`, while the page and builder sites hold a row with `.id` (a
 *   NUMBER). `(id, target)` is the one shape all five can call as they stand, and the
 *   template renders a string id and a number id IDENTICALLY (asserted in the verify).
 *
 * ── WHAT THIS CANNOT REACH ────────────────────────────────────────────────────────────
 * ⛔ `server/server.js` spells the synthetic THREE more times (validateImplTarget's escape
 *   hatch :1461, its message :1480, and the 409 uniqueness check :2309) and CANNOT import
 *   this file — THE WALL (§6). That divergence is UNAVOIDABLE, and it is named here rather
 *   than left to be discovered: if the spelling ever changes, the server must change too.
 */

/** The synthetic IS-line key an implementation line emits when it is not routed elsewhere. */
export function implSyntheticKey(id) {
  return `rev_impl_${id}`;
}

/** The IS-line key this implementation line's revenue is deposited at. */
export function implTargetKey(id, target) {
  return target || implSyntheticKey(id);
}

/** True when this line is routed to a leaf OTHER than its own synthetic.
 *  ⛔ NULL and the sentinel are both FALSE here — they are the same state to the engine. */
export function isRoutedElsewhere(id, target) {
  return !!target && target !== implSyntheticKey(id);
}

/* -- W-081 (2026-10-05): THE DEFERRED-REVENUE (BALANCE SHEET) SIDE OF AN IMPLEMENTATION LINE ------------------------------
 * implementation_lines.bs_line_key holds the operator's answer to "which balance-sheet line carries this DEFERRED line's
 * balance?", in the same three states as target_gl_line_key:
 *   NULL                     UNANSWERED  -> blank and REQUIRED (P6 blocks go-live); the engine books the synthetic meanwhile
 *   'deferred_rev_impl_<id>' SYNTHETIC   -> "Synthetic (forecast-only)": a new stream with no account yet (the old behaviour)
 *   any other key            LINKED      -> the client's REAL GL deferred-revenue account (a bs_actuals_meta leaf). The
 *                                           builder wires that leaf to the engine's balance and NO synthetic row is made, so
 *                                           the account's history continues across the close with no phantom cash.
 * The sentinel is non-NULL for the target_gl_line_key reason: the PUT is COALESCE-on-omit and cannot write NULL back.
 * The server spells implDrSyntheticKey a second time (THE WALL) -- verify-wall-mirrors pair M10. */

/** The synthetic balance-sheet key (and the "Synthetic (forecast-only)" answer) of a deferred implementation line. */
export function implDrSyntheticKey(id) {
  return `deferred_rev_impl_${id}`;
}

/** The IS_REF key computeBS's module_link driver reads for a LINKED line's deferred balance. */
export function implDrRefKey(id) {
  return `defrev_impl_${id}`;
}

/** The real account a line is linked to, or null (unanswered, or the synthetic sentinel). */
export function implDrLinkedKey(id, bsLineKey) {
  return bsLineKey && bsLineKey !== implDrSyntheticKey(id) ? bsLineKey : null;
}

/** WHICH balance-sheet row holds this line's deferred balance, read off the BUILT skeleton: the leaf the builder wired to it
 *  (module_link_ref === implDrRefKey(id)), else its synthetic. ONE answer for the orchestrator (where the balance is put) and
 *  P16 (where it is read back), so the two cannot disagree. */
export function implDrBsKey(id, bsLines) {
  const ref = implDrRefKey(id);
  const wired = (bsLines || []).find(l => l && l.module_link_ref === ref);
  return wired ? wired.id : implDrSyntheticKey(id);
}
